import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, Subscription, interval, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  SupportColumn,
  SupportMailAttachment,
  SupportMailFilter,
  SupportMailMessage,
  SupportMailOrderRef,
  SupportMailPartner,
  SupportMailService,
  PurchasingMailService,
  SupportMailThreadDetail,
  SupportMailThreadSummary,
  SupportMailTicketRef,
  SupportMailboxSettings,
  SupportService,
  SupportTicketPriority,
} from '../../api';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../auth/auth.service';
import { MENU_SETTINGS, userHasMenu } from '../../../auth/menu-access';

// ============================================================================
// Support → Levelezés: a support postafiók beszélgetései három hasábban —
// lista, beszélgetés + válasz, jobb oldalt a kapcsolatok (vevő, rendelések,
// taskok). Beszélgetés azonosító: L-<id>, task: T-<id>.
// ============================================================================

interface MailFilterVM {
  key: SupportMailFilter;
  label: string;
}

/** GET orders?search= válasz egy eleme (csak a felhasznált mezők) */
interface OrderListItemDto {
  id?: number;
  receipt_id?: number;
  customer?: string;
  merchant_id?: number;
  etsy_shop_id?: number;
  is_archived?: number;
  source_key?: string;
  is_manual?: number;
  merchant?: { name?: string } | null;
}

interface SettingsForm {
  enabled: boolean;
  email: string;
  from_name: string;
  username: string;
  password: string;
  imap_host: string;
  imap_port: number | string;
  imap_secure: boolean;
  smtp_host: string;
  smtp_port: number | string;
  smtp_secure: boolean;
  signature: string;
}

interface ComposeForm {
  to: string;
  cc: string;
  subject: string;
  text: string;
  files: File[];
  /** a címzett mentése a partnerek közé ezen a néven (üres = nem ment) */
  saveAsPartner: boolean;
  partnerName: string;
}

interface PartnerForm {
  id: number | null;
  name: string;
  email: string;
  note: string;
}

interface TaskForm {
  title: string;
  priority: SupportTicketPriority;
  status: string;
}

const FILTERS: MailFilterVM[] = [
  { key: 'open', label: 'Nyitott' },
  { key: 'waiting', label: 'Válaszra vár' },
  { key: 'unread', label: 'Olvasatlan' },
  { key: 'partner', label: 'Partnerek' },
  { key: 'closed', label: 'Lezárt' },
  { key: 'spam', label: 'Spam' },
];

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 10;
const AVATAR_PALETTE = ['#C77B9B', '#5E7FA8', '#6E9663', '#B9834A', '#9A6E9E', '#C9A45C'];

/** A beérkezett HTML kerete: script nélküli iframe-be kerül, saját CSP-vel. */
const FRAME_HEAD = '<!doctype html><html><head><meta charset="utf-8">'
  + '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src data: https: http:; style-src \'unsafe-inline\'; font-src data: https:">'
  + '<base target="_blank">'
  + '<style>html,body{margin:0;padding:0}body{font-family:Nunito,Arial,Helvetica,sans-serif;font-size:14px;line-height:1.55;color:#21252B;word-break:break-word;overflow:hidden}'
  + 'img{max-width:100%;height:auto}table{max-width:100%}a{color:#4C7A2E}'
  + 'blockquote{margin:8px 0 0;padding-left:12px;border-left:3px solid #E4E7DD;color:#707870}</style></head><body>';

@Component({
  selector: 'app-support-mail',
  templateUrl: './support-mail.component.html',
  styleUrl: './support-mail.component.scss',
})
export class SupportMailComponent implements OnInit, OnDestroy {
  readonly filters = FILTERS;
  readonly prioKeys: SupportTicketPriority[] = ['P1', 'P2', 'P3'];
  readonly nameToken = '{{nev}}';
  readonly signaturePlaceholder = 'Üdvözlettel,\n{{nev}}\nBoardGeekFox ügyfélszolgálat\nsupport@bgfox.hu';

  settings: SupportMailboxSettings | null = null;

  // Lista
  filter: SupportMailFilter = 'open';
  search = '';
  threads: SupportMailThreadSummary[] = [];
  total = 0;
  loadingList = true;
  syncing = false;

  // Beszélgetés
  thread: SupportMailThreadDetail | null = null;
  loadingThread = false;
  replyText = '';
  replyCc = '';
  replyCcOpen = false;
  replyFiles: File[] = [];
  sending = false;
  private readonly frameCache = new Map<number, SafeHtml>();

  // Jobb oldali panel
  orderSearch = '';
  orderResults: SupportMailOrderRef[] = [];
  ticketSearch = '';
  ticketResults: SupportMailTicketRef[] = [];
  taskForm: TaskForm | null = null;
  taskSaving = false;
  columns: SupportColumn[] = [];
  linking = false;

  // Partnerek (címjegyzék)
  partners: SupportMailPartner[] = [];
  partnersOpen = false;
  partnerForm: PartnerForm | null = null;
  partnerSaving = false;
  /** „Mentés partnerként" a beszélgetés jobb oldalán */
  quickPartnerName: string | null = null;

  // Új levél + beállítások
  compose: ComposeForm | null = null;
  settingsForm: SettingsForm | null = null;
  settingsSaving = false;
  settingsTesting = false;
  testResult: { imap: string | null; smtp: string | null } | null = null;

  @ViewChild('messagesEl') messagesEl?: ElementRef<HTMLDivElement>;

  private readonly search$ = new Subject<string>();
  private readonly orderSearch$ = new Subject<string>();
  private readonly ticketSearch$ = new Subject<string>();
  private readonly subs: Subscription[] = [];
  private pendingThreadId: number | null = null;

  /** support (Support → Levelezés) vagy beszerzés (Beszerzés → Levelezés) — az útvonal dönt */
  readonly purchasing: boolean;
  /** beszerzési módban: a szálhoz kötött beszerzési rendelések */
  threadOrders: { id: number; displayId: string; statusLabel: string; supplier: { name: string } | null; grossTotal: number | null; items: { name: string; quantity: number }[] }[] = [];
  private mail: SupportMailService;

  constructor(
    supportMail: SupportMailService,
    purchasingMail: PurchasingMailService,
    private support: SupportService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private notification: NotificationService,
    private auth: AuthService,
  ) {
    this.purchasing = this.route.snapshot.data['mailbox'] === 'purchasing';
    this.mail = this.purchasing ? purchasingMail : supportMail;
  }

  /** A postafiók (jelszó, szerverek, aláírás) csak a Beállítások joggal szerkeszthető. */
  get canEditMailbox(): boolean {
    return userHasMenu(this.auth.user, MENU_SETTINGS);
  }

  ngOnInit(): void {
    this.mail.getSettings().subscribe({
      next: (settings) => {
        this.settings = settings;
        if (!settings.configured && this.canEditMailbox) {
          this.openSettings();
        }
      },
      error: () => undefined,
    });
    this.loadPartners();
    if (!this.purchasing) this.support.getColumns().pipe(catchError(() => of<SupportColumn[]>([]))).subscribe((columns) => (this.columns = columns));

    this.subs.push(
      this.route.queryParamMap.subscribe((params) => {
        const id = Number(params.get('thread'));
        if (Number.isInteger(id) && id > 0 && id !== this.thread?.id) {
          this.pendingThreadId = id;
          this.openThreadById(id);
        }
      }),
      this.search$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(() => this.loadList()),
      // A lista percenként frissül (a szerver percenként húzza be az új leveleket).
      interval(60000).subscribe(() => this.loadList(true)),
      this.orderSearch$
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
          switchMap((query) => {
            if (!query.trim()) {
              return of<OrderListItemDto[]>([]);
            }
            const params = new URLSearchParams({ search: query.trim(), archived: 'all', page: '0', page_size: '5' });
            return this.http.get<{ items: OrderListItemDto[] }>(`orders?${params.toString()}`).pipe(
              map((response) => response?.items || []),
              catchError(() => of<OrderListItemDto[]>([])),
            );
          }),
        )
        .subscribe((items) => {
          const linked = new Set((this.thread?.orders || []).map((o) => o.orderId));
          this.orderResults = items
            .filter((order) => !!order.id && !linked.has(order.id))
            .map((order) => ({
              orderId: order.id as number,
              displayId: `#${order.receipt_id ?? ''}`,
              customerName: order.customer || 'Ismeretlen vevő',
              shop: `${this.merchantName(order)}${order.is_archived === 1 ? ' · archív' : ''}`,
            }));
        }),
      this.ticketSearch$
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
          switchMap((query) => (query.trim() ? this.mail.searchTickets(query).pipe(catchError(() => of<SupportMailTicketRef[]>([]))) : of<SupportMailTicketRef[]>([]))),
        )
        .subscribe((items) => {
          const linked = new Set((this.thread?.tickets || []).map((t) => t.id));
          this.ticketResults = items.filter((ticket) => !linked.has(ticket.id));
        }),
    );
    this.loadList();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  // ------------------------------------------------------------------- lista

  loadList(silent = false): void {
    if (!silent) {
      this.loadingList = true;
    }
    this.mail.listThreads(this.filter, this.search).subscribe({
      next: (result) => {
        this.threads = result.items;
        this.total = result.total;
        this.loadingList = false;
        this.mail.refreshUnread();
      },
      error: () => {
        this.loadingList = false;
        if (!silent) {
          this.notification.show('A levelezés nem tölthető be.', 'danger');
        }
      },
    });
  }

  setFilter(filter: SupportMailFilter): void {
    if (this.filter === filter) {
      return;
    }
    this.filter = filter;
    this.loadList();
  }

  onSearchChange(value: string): void {
    this.search = value;
    this.search$.next(value);
  }

  syncNow(): void {
    if (this.syncing) {
      return;
    }
    this.syncing = true;
    this.mail.sync().subscribe({
      next: (result) => {
        this.syncing = false;
        this.notification.show(result.imported ? `${result.imported} új levél érkezett.` : 'Nincs új levél.', 'success');
        this.loadList(true);
        if (this.thread) {
          this.openThreadById(this.thread.id, true);
        }
        this.mail.getSettings().subscribe((settings) => (this.settings = settings));
      },
      error: (err) => {
        this.syncing = false;
        this.notification.show(this.errorText(err, 'A postafiók nem érhető el.'), 'danger');
      },
    });
  }

  // ------------------------------------------------------------- beszélgetés

  openThread(summary: SupportMailThreadSummary): void {
    if (this.hasDraft() && summary.id !== this.thread?.id && !confirm('A megkezdett válasz elvész. Megnyitod a másik beszélgetést?')) {
      return;
    }
    this.router.navigate([], { relativeTo: this.route, queryParams: { thread: summary.id }, replaceUrl: true });
    this.openThreadById(summary.id);
  }

  private openThreadById(id: number, keepDraft = false): void {
    this.loadingThread = !keepDraft;
    this.mail.getThread(id).subscribe({
      next: (thread) => {
        this.loadingThread = false;
        this.pendingThreadId = null;
        this.setThread(thread, keepDraft);
        const row = this.threads.find((t) => t.id === id);
        if (row) {
          row.unread = false;
        }
      },
      error: (err) => {
        this.loadingThread = false;
        this.notification.show(this.errorText(err, 'A beszélgetés nem található.'), 'danger');
      },
    });
  }

  private setThread(thread: SupportMailThreadDetail, keepDraft = false): void {
    const changed = this.thread?.id !== thread.id;
    this.thread = thread;
    if (this.purchasing && changed) {
      this.threadOrders = [];
      this.http.get<any[]>('purchasing/orders', { params: { thread: `${thread.id}` } }).pipe(catchError(() => of([]))).subscribe((orders) => {
        if (this.thread?.id === thread.id) this.threadOrders = orders || [];
      });
    }
    if (changed && !keepDraft) {
      this.replyText = '';
      this.replyCc = '';
      this.replyCcOpen = false;
      this.replyFiles = [];
      this.taskForm = null;
      this.quickPartnerName = null;
      this.orderSearch = '';
      this.orderResults = [];
      this.ticketSearch = '';
      this.ticketResults = [];
      this.frameCache.clear();
    }
    const row = this.threads.find((t) => t.id === thread.id);
    if (row) {
      Object.assign(row, {
        status: thread.status, unread: thread.unread, preview: thread.preview, lastMessageAt: thread.lastMessageAt,
        lastDirection: thread.lastDirection, messageCount: thread.messageCount, orderCount: thread.orders.length, ticketCount: thread.tickets.length,
      });
    }
    setTimeout(() => {
      const el = this.messagesEl?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 60);
  }

  closeThreadView(): void {
    this.thread = null;
    this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
  }

  toggleStatus(): void {
    if (!this.thread) {
      return;
    }
    const status = this.thread.status === 'open' ? 'closed' : 'open';
    this.mail.updateThread(this.thread.id, { status }).subscribe({
      next: (thread) => {
        this.setThread(thread, true);
        this.notification.show(status === 'closed' ? 'Beszélgetés lezárva.' : 'Beszélgetés újranyitva.', 'success');
        this.loadList(true);
      },
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült.'), 'danger'),
    });
  }

  /** Spam mappába tesz (nem a lezártak közé) / visszahoz a beérkezők közé. */
  setSpam(spam: boolean): void {
    if (!this.thread) {
      return;
    }
    this.mail.updateThread(this.thread.id, { status: spam ? 'spam' : 'open' }).subscribe({
      next: (thread) => {
        this.notification.show(spam ? 'Áttéve a Spam mappába.' : 'Visszakerült a nyitott beszélgetések közé.', 'success');
        if (spam) {
          this.closeThreadView();
        } else {
          this.setThread(thread, true);
        }
        this.loadList(true);
      },
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült.'), 'danger'),
    });
  }

  /** Feladó tiltása: az új levelei egyenesen a Spam mappába érkeznek. */
  setSenderBlocked(blocked: boolean): void {
    if (!this.thread) {
      return;
    }
    this.mail.blockSender(this.thread.id, blocked).subscribe({
      next: (thread) => {
        this.setThread(thread, true);
        this.notification.show(blocked ? 'Feladó tiltva — az új levelei a Spam mappába mennek.' : 'A feladó tiltása feloldva.', 'success');
        this.loadList(true);
      },
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült.'), 'danger'),
    });
  }

  markUnread(): void {
    if (!this.thread) {
      return;
    }
    this.mail.updateThread(this.thread.id, { unread: true }).subscribe({
      next: () => {
        this.closeThreadView();
        this.loadList(true);
      },
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült.'), 'danger'),
    });
  }

  /** A beérkezett HTML levél sandboxolt iframe-be kerül (script nem fut benne). */
  frameDoc(message: SupportMailMessage): SafeHtml {
    let doc = this.frameCache.get(message.id);
    if (!doc) {
      doc = this.sanitizer.bypassSecurityTrustHtml(`${FRAME_HEAD}${message.html || ''}</body></html>`);
      this.frameCache.set(message.id, doc);
    }
    return doc;
  }

  /** Az iframe magassága a tartalomhoz igazodik (a képek betöltése után is). */
  fitFrame(event: Event): void {
    const frame = event.target as HTMLIFrameElement;
    const resize = () => {
      const doc = frame.contentDocument;
      if (doc?.documentElement) {
        frame.style.height = `${Math.min(Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0), 4000)}px`;
      }
    };
    resize();
    try {
      Array.from(frame.contentDocument?.images || []).forEach((img) => img.addEventListener('load', resize));
    } catch {
      // a keret nem elérhető — marad az alapmagasság
    }
    setTimeout(resize, 400);
  }

  download(attachment: SupportMailAttachment): void {
    this.mail.downloadAttachment(attachment.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.fileName;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      },
      error: () => this.notification.show('A csatolmány nem tölthető le.', 'danger'),
    });
  }

  // ------------------------------------------------------------------ válasz

  onReplyFiles(event: Event): void {
    this.replyFiles = this.addFiles(this.replyFiles, event);
  }

  onComposeFiles(event: Event): void {
    if (this.compose) {
      this.compose.files = this.addFiles(this.compose.files, event);
    }
  }

  private addFiles(current: File[], event: Event): File[] {
    const input = event.target as HTMLInputElement;
    const out = [...current];
    for (const file of Array.from(input.files || [])) {
      if (file.size > MAX_FILE_BYTES) {
        this.notification.show(`„${file.name}" túl nagy (legfeljebb 15 MB).`, 'warning');
        continue;
      }
      if (out.length >= MAX_FILES) {
        this.notification.show(`Egy levélhez legfeljebb ${MAX_FILES} fájl csatolható.`, 'warning');
        break;
      }
      out.push(file);
    }
    input.value = '';
    return out;
  }

  removeReplyFile(index: number): void {
    this.replyFiles = this.replyFiles.filter((_, i) => i !== index);
  }

  removeComposeFile(index: number): void {
    if (this.compose) {
      this.compose.files = this.compose.files.filter((_, i) => i !== index);
    }
  }

  sendReply(close = false): void {
    if (!this.thread || this.sending || !this.replyText.trim()) {
      return;
    }
    this.sending = true;
    this.mail.reply(this.thread.id, { text: this.replyText, cc: this.replyCc, close }, this.replyFiles).subscribe({
      next: (thread) => {
        this.sending = false;
        this.replyText = '';
        this.replyCc = '';
        this.replyCcOpen = false;
        this.replyFiles = [];
        this.setThread(thread, true);
        this.notification.show(close ? 'Válasz elküldve, beszélgetés lezárva.' : 'Válasz elküldve.', 'success');
        this.loadList(true);
      },
      error: (err) => {
        this.sending = false;
        this.notification.show(this.errorText(err, 'A levelet nem sikerült elküldeni.'), 'danger');
      },
    });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.compose && !this.sending) {
        this.compose = null;
      } else if (this.partnersOpen) {
        this.partnersOpen = false;
      } else if (this.settingsForm && !this.settingsSaving) {
        this.settingsForm = null;
      }
    }
  }

  onReplyKeydown(event: KeyboardEvent): void {
    // Ctrl/Cmd + Enter = küldés
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.sendReply();
    }
  }

  private hasDraft(): boolean {
    return !!this.replyText.trim() || this.replyFiles.length > 0;
  }

  // ---------------------------------------------------------------- új levél

  openCompose(to = ''): void {
    this.partnersOpen = false;
    this.compose = { to, cc: '', subject: '', text: '', files: [], saveAsPartner: false, partnerName: '' };
  }

  /** Partner-javaslatok a címzett mezőhöz (csak teljes címek; a begépelt szövegre szűrve). */
  get composeSuggestions(): SupportMailPartner[] {
    const form = this.compose;
    if (!form) {
      return [];
    }
    const q = form.to.trim().toLowerCase();
    return this.partners
      .filter((p) => !p.email.startsWith('@') && p.email !== q)
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.email.includes(q))
      .slice(0, 8);
  }

  pickComposePartner(partner: SupportMailPartner): void {
    if (this.compose) {
      this.compose.to = partner.email;
      this.compose.saveAsPartner = false;
    }
  }

  /** A begépelt címzett már partner? (ilyenkor nem kínáljuk a mentést) */
  get composeToIsPartner(): boolean {
    const to = (this.compose?.to || '').trim().toLowerCase();
    return !!to && this.partners.some((p) => p.email === to || (p.email.startsWith('@') && to.endsWith(p.email)));
  }

  sendCompose(): void {
    const form = this.compose;
    if (!form || this.sending) {
      return;
    }
    if (!form.to.trim() || !form.subject.trim() || !form.text.trim()) {
      this.notification.show('A címzett, a tárgy és az üzenet kötelező.', 'warning');
      return;
    }
    this.sending = true;
    if (form.saveAsPartner && !form.partnerName.trim()) {
      this.notification.show('Add meg a partner nevét a mentéshez.', 'warning');
      return;
    }
    const partnerName = form.saveAsPartner && !this.composeToIsPartner ? form.partnerName.trim() : undefined;
    this.mail.compose({ to: form.to, cc: form.cc, subject: form.subject, text: form.text, partnerName }, form.files).subscribe({
      next: (thread) => {
        this.sending = false;
        this.compose = null;
        if (partnerName) {
          this.loadPartners();
        }
        this.notification.show('Levél elküldve.', 'success');
        this.router.navigate([], { relativeTo: this.route, queryParams: { thread: thread.id }, replaceUrl: true });
        this.setThread(thread);
        this.loadList(true);
      },
      error: (err) => {
        this.sending = false;
        this.notification.show(this.errorText(err, 'A levelet nem sikerült elküldeni.'), 'danger');
      },
    });
  }

  // --------------------------------------------------------------- rendelések

  onOrderSearchChange(value: string): void {
    this.orderSearch = value;
    if (!value.trim()) {
      this.orderResults = [];
    }
    this.orderSearch$.next(value);
  }

  addOrder(order: SupportMailOrderRef): void {
    if (!this.thread || this.linking) {
      return;
    }
    this.linking = true;
    this.mail.addOrder(this.thread.id, order.orderId).subscribe({
      next: (thread) => {
        this.linking = false;
        this.orderSearch = '';
        this.orderResults = [];
        this.orderSearch$.next('');
        this.setThread(thread, true);
      },
      error: (err) => {
        this.linking = false;
        this.notification.show(this.errorText(err, 'A rendelés nem kapcsolható.'), 'danger');
      },
    });
  }

  removeOrder(order: SupportMailOrderRef): void {
    if (!this.thread) {
      return;
    }
    this.mail.removeOrder(this.thread.id, order.orderId).subscribe({
      next: (thread) => this.setThread(thread, true),
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült.'), 'danger'),
    });
  }

  openOrder(order: SupportMailOrderRef): void {
    this.router.navigate(['/orders', order.orderId]);
  }

  // ------------------------------------------------------------------ taskok

  openTaskForm(): void {
    if (!this.thread) {
      return;
    }
    const firstOpen = this.columns.find((c) => !c.isDone) || this.columns[0];
    this.taskForm = { title: this.thread.subject.replace(/^\s*((re|fw|fwd|válasz)\s*:\s*)+/i, ''), priority: 'P2', status: firstOpen?.name || 'Új' };
  }

  createTask(): void {
    const form = this.taskForm;
    const thread = this.thread;
    if (!form || !thread || this.taskSaving || !form.title.trim()) {
      return;
    }
    this.taskSaving = true;
    const who = thread.customerName ? `${thread.customerName} <${thread.customerEmail}>` : thread.customerEmail;
    this.support.createTicket({
      title: form.title.trim(),
      description: `Levelezésből nyitva: ${thread.displayId} · ${who}`,
      priority: form.priority,
      status: form.status,
      orderId: thread.orders[0]?.orderId ?? null,
      mailThreadIds: [thread.id],
    }).subscribe({
      next: (ticket) => {
        this.taskSaving = false;
        this.taskForm = null;
        this.notification.show(`${ticket.displayId} task létrehozva.`, 'success');
        this.openThreadById(thread.id, true);
      },
      error: (err) => {
        this.taskSaving = false;
        this.notification.show(this.errorText(err, 'A task nem hozható létre.'), 'danger');
      },
    });
  }

  onTicketSearchChange(value: string): void {
    this.ticketSearch = value;
    if (!value.trim()) {
      this.ticketResults = [];
    }
    this.ticketSearch$.next(value);
  }

  addTicket(ticket: SupportMailTicketRef): void {
    if (!this.thread || this.linking) {
      return;
    }
    this.linking = true;
    this.mail.addTicket(this.thread.id, ticket.id).subscribe({
      next: (thread) => {
        this.linking = false;
        this.ticketSearch = '';
        this.ticketResults = [];
        this.ticketSearch$.next('');
        this.setThread(thread, true);
      },
      error: (err) => {
        this.linking = false;
        this.notification.show(this.errorText(err, 'A task nem kapcsolható.'), 'danger');
      },
    });
  }

  removeTicket(ticket: SupportMailTicketRef): void {
    if (!this.thread) {
      return;
    }
    this.mail.removeTicket(this.thread.id, ticket.id).subscribe({
      next: (thread) => this.setThread(thread, true),
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült.'), 'danger'),
    });
  }

  openTicket(ticket: SupportMailTicketRef): void {
    this.router.navigate(['/support'], { queryParams: { ticket: ticket.id } });
  }

  // ---------------------------------------------------------------- partnerek

  private loadPartners(): void {
    this.mail.listPartners().pipe(catchError(() => of<SupportMailPartner[]>([]))).subscribe((partners) => (this.partners = partners));
  }

  openPartners(): void {
    this.partnersOpen = true;
    this.partnerForm = null;
    this.loadPartners();
  }

  editPartner(partner?: SupportMailPartner): void {
    this.partnerForm = partner
      ? { id: partner.id, name: partner.name, email: partner.email, note: partner.note }
      : { id: null, name: '', email: '', note: '' };
  }

  savePartnerForm(): void {
    const form = this.partnerForm;
    if (!form || this.partnerSaving) {
      return;
    }
    this.partnerSaving = true;
    this.mail.savePartner(form.id, { name: form.name, email: form.email, note: form.note }).subscribe({
      next: () => {
        this.partnerSaving = false;
        this.partnerForm = null;
        this.afterPartnersChanged();
      },
      error: (err) => {
        this.partnerSaving = false;
        this.notification.show(this.errorText(err, 'A partner nem menthető.'), 'danger');
      },
    });
  }

  removePartner(partner: SupportMailPartner): void {
    if (!confirm(`Törlöd a partnert: ${partner.name}? (A levelezése megmarad.)`)) {
      return;
    }
    this.mail.deletePartner(partner.id).subscribe({
      next: () => this.afterPartnersChanged(),
      error: (err) => this.notification.show(this.errorText(err, 'Nem sikerült törölni.'), 'danger'),
    });
  }

  /** A nyitott beszélgetés másik felének mentése partnerként (jobb oldali panel). */
  saveQuickPartner(): void {
    const thread = this.thread;
    const name = (this.quickPartnerName || '').trim();
    if (!thread || !name || this.partnerSaving) {
      return;
    }
    this.partnerSaving = true;
    this.mail.savePartner(null, { name, email: thread.customerEmail }).subscribe({
      next: () => {
        this.partnerSaving = false;
        this.quickPartnerName = null;
        this.notification.show(`${name} elmentve a partnerek közé.`, 'success');
        this.afterPartnersChanged();
      },
      error: (err) => {
        this.partnerSaving = false;
        this.notification.show(this.errorText(err, 'A partner nem menthető.'), 'danger');
      },
    });
  }

  private afterPartnersChanged(): void {
    this.loadPartners();
    this.loadList(true);
    if (this.thread) {
      this.openThreadById(this.thread.id, true);
    }
  }

  // ------------------------------------------------------------- beállítások

  openSettings(): void {
    const s = this.settings;
    this.testResult = null;
    this.settingsForm = {
      enabled: s?.enabled ?? true,
      email: s?.email || '',
      from_name: s?.from_name || '',
      username: s?.username || '',
      password: '',
      imap_host: s?.imap_host || '',
      imap_port: s?.imap_port ?? 993,
      imap_secure: s ? s.imap_secure : true,
      smtp_host: s?.smtp_host || '',
      smtp_port: s?.smtp_port ?? 587,
      smtp_secure: s ? s.smtp_secure : false,
      signature: s?.signature || '',
    };
  }

  saveSettings(thenTest = false): void {
    const form = this.settingsForm;
    if (!form || this.settingsSaving) {
      return;
    }
    this.settingsSaving = true;
    const { password, ...rest } = form;
    // Üres jelszómező = a tárolt jelszó marad.
    const payload = password ? { ...rest, password } : rest;
    this.mail.updateSettings(payload).subscribe({
      next: (settings) => {
        this.settingsSaving = false;
        this.settings = settings;
        form.password = '';
        if (thenTest) {
          this.testSettings();
        } else {
          this.notification.show('Postafiók-beállítások mentve.', 'success');
          this.settingsForm = null;
          if (settings.configured && settings.enabled) {
            this.syncNow();
          }
        }
      },
      error: (err) => {
        this.settingsSaving = false;
        this.notification.show(this.errorText(err, 'A mentés nem sikerült.'), 'danger');
      },
    });
  }

  private testSettings(): void {
    this.settingsTesting = true;
    this.testResult = null;
    this.mail.testSettings().subscribe({
      next: (result) => {
        this.settingsTesting = false;
        this.testResult = result;
      },
      error: (err) => {
        this.settingsTesting = false;
        this.notification.show(this.errorText(err, 'A kapcsolat-teszt nem futott le.'), 'danger');
      },
    });
  }

  // ------------------------------------------------------------------ segédek

  whoOf(item: { customerName: string; customerEmail: string; partnerName?: string | null }): string {
    return item.customerName || item.partnerName || item.customerEmail;
  }

  initialOf(name: string): string {
    return (name || '?').trim().charAt(0).toUpperCase() || '?';
  }

  colorOf(name: string): string {
    let hash = 0;
    for (const ch of name || '') {
      hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    }
    return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
  }

  /** Ma: „14:32", idén: „szept. 18.", régebben évvel. */
  shortTime(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('hu-HU', date.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' });
  }

  fullTime(iso: string): string {
    const date = new Date(iso);
    return isNaN(date.getTime()) ? '' : date.toLocaleString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  sizeLabel(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  }

  senderOf(message: SupportMailMessage): string {
    if (message.direction === 'out') {
      return message.author || message.fromName || 'Support';
    }
    return message.fromName || message.fromEmail;
  }

  recipientsOf(message: SupportMailMessage): string {
    const to = (message.to || []).map((a) => a.email).join(', ');
    const cc = (message.cc || []).map((a) => a.email).join(', ');
    return cc ? `${to} · másolat: ${cc}` : to;
  }

  trackThread(_: number, thread: SupportMailThreadSummary): number {
    return thread.id;
  }

  trackMessage(_: number, message: SupportMailMessage): number {
    return message.id;
  }

  private errorText(err: unknown, fallback: string): string {
    const message = (err as { error?: { message?: string | string[] } })?.error?.message;
    if (Array.isArray(message)) {
      return message.join(' ');
    }
    return message || fallback;
  }

  private merchantName(order: OrderListItemDto): string {
    if (order?.merchant_id === 1 && order?.etsy_shop_id === 2) {
      return 'Jump';
    }
    if (order?.source_key === 'manual' || order?.is_manual === 1) {
      return 'BGFox';
    }
    return order?.merchant?.name || 'Shop';
  }
}
